'use strict'
import React, { useState, useEffect, useCallback } from 'react'
import { SpinnerButton } from '../../../common/CustomButton.js'
import { registerLocale } from 'react-datepicker'
import ja from 'date-fns/locale/ja'
import 'react-datepicker/dist/react-datepicker.css'
import { getMessage } from '../../../../common/utils/MessageUtils.js'

// 型定義
import '../extra/types.js'

// 日本語ロケールを登録
registerLocale('ja', ja)

// 禁則文字リスト
// const disallowedChars = [',', '\\', '"']

/*==================================
    ユーティリティ関数
  ===================================*/

/**
 * Description placeholder
 *
 * @param {{ errors: any; field?: string }} param0
 * @param {any} param0.errors: ErrorMessage
 * @param {string} [param0.field=''] Default is `''`
 * @returns {any}
 */
export const ShowErrors = React.memo(({ errors: errorMessage, field = '' }) => {
  if (!errorMessage?.[field]) return <></>

  return (
    <>
      {errorMessage[field].map((err, index) => (
        <p key={index} className="red--text">
          {err}
        </p>
      ))}
    </>
  )
})

/**
 * @function FormInput
 * @param {FormInputProps} props
 */
const FormInput = React.memo(
  ({ id, label, required = true, placeholder, value, errors, disabled, onChange, onBlur }) => {
    const hasError = errors && errors[id] && errors[id].length > 0

    return (
      <tr>
        <th className="form-table__col-head w-[150px]">
          <label htmlFor={id} className="text-h7 font-bold">
            {label} {required && <span className="red--text">*</span>}
          </label>
        </th>
        <td className="form-control form-table__col-data">
          <input
            id={id}
            name={id}
            className={`text-input w-full text-left ${hasError ? 'is-error' : ''}`}
            type="text"
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            placeholder={placeholder}
            disabled={disabled}
          />
          <ShowErrors field={id} errors={errors} />
        </td>
      </tr>
    )
  }
)

const CategoryNameModalContent = (props) => {
  const { categoryNamesInfo, editMode, updateData, onSubmit, onClose } = props
  const initial = { id: crypto.randomUUID(), categoryKey: '', categoryName: '', applyNum: 0 }
  const initData = {
    ...initial,
    ...updateData
  }

  const [formData, setFormData] = useState(initial)
  const [errors, setErrorState] = useState({})

  useEffect(() => {
    setFormData(initData)
  }, [updateData])

  useEffect(() => {
    const modalWindowElement = document.querySelector('.modal-window')
    const modalWindowClassList = ['w-[initial]', 'max-h-[97vh]', 'max-w-[750px]', 'overflow-y-auto']

    modalWindowElement?.classList.add(...modalWindowClassList)
    return () => {
      modalWindowElement?.classList.remove(...modalWindowClassList)
    }
  }, [])

  /*==================================
    イベントハンドラ
  ===================================*/
  /*  ===== 用途タグ設定ダイアログ ===== */
  /**
   * 入力値変更（onChange）
   *
   * @param {React.ChangeEvent<HTMLInputElement>} e - イベントオブジェクト
   */
  const handleChange = useCallback(
    (e) => {
      const { name, value } = e.target
      setFormData((prev) => ({ ...prev, [name]: value }))
    },
    [setFormData]
  )

  /**
   * 入力値変更（onBlur）
   *
   * @param {React.FocusEvent<HTMLInputElement>} e - フォーカスが外れた時のイベントオブジェクト
   */
  const handleBlur = useCallback(
    (e) => {
      const { name, value } = e.target
      const trimmedValue = value.trim()
      if (value !== trimmedValue) {
        setFormData((prev) => ({ ...prev, [name]: trimmedValue }))
      }
    },
    [setFormData]
  )

  /** ボタン押下またはフォーム送信 */
  const handleSubmit = (event) => {
    if (event) event.preventDefault() // フォーム送信のデフォルト動作をキャンセル

    // チェック
    const validationErrors = formHasErrors()
    if (validationErrors) {
      setErrorState(validationErrors)
      return
    }

    setErrorState({})

    const submitData = {
      ...formData,
      categoryKey: formData.categoryKey,
      categoryName: formData.categoryName.trim(),
      applyNum: formData.applyNum
    }

    onSubmit?.(submitData, setFormData)
  }

  /*==================================
    入力チェック
  ===================================*/

  /**
   * 禁則文字チェック関数
   *
   * @param {string} input - 入力値
   * @param {string[]} chars - 禁則文字リスト
   * @returns {boolean}
   */
  function checkIfCharsExist(input = '', chars = disallowedChars) {
    return chars.some((char) => input.includes(char))
  }

  /**
   * 入力チェック関数
   *
   * @returns {false | Errors} エラーがなければfalse、あればErrorsオブジェクトを返却
   */
  const formHasErrors = () => {
    const errors = {
      categoryName: []
    }

    // 必須チェック
    if (formData.categoryName?.trim() === '') {
      errors.categoryName.push(getMessage('E010001', '用途タグ名'))
    }

    // 桁数チェック
    if (formData.categoryName?.length > 100) {
      errors.categoryName.push(getMessage('E010006', '用途タグ名', '100'))
    }

    // 禁則文字チェック
    // if (checkIfCharsExist(formData.categoryName || '')) {
    //   const charString = disallowedChars.join(' ')
    //   errors.categoryName.push(getMessage('E010016', charString))
    // }

    // 重複チェック
    const predicate = (registeredData) => {
      let exists = registeredData.categoryName === formData?.categoryName

      if (editMode) {
        exists = exists && registeredData.id !== formData?.id
      }
      return exists
    }

    if (Object.values(categoryNamesInfo).find(predicate)) {
      errors.categoryName.push(getMessage('E010013', formData?.categoryName))
    }

    if (Object.values(errors).some((e) => e.length > 0)) return errors

    return false
  }

  return (
    <>
      <form className="text-base" onSubmit={handleSubmit}>
        <table className="form-table table-fixed w-full">
          <tbody>
            {/* =======================用途タグ名====================== */}
            <FormInput
              errors={errors}
              value={formData.categoryName}
              onChange={handleChange}
              onBlur={handleBlur}
              id="categoryName"
              name="categoryName"
              label="用途タグ名"
              placeholder="用途タグ名を入力してください"
            />
            {/* =======================用途タグ名====================== */}
          </tbody>
        </table>
      </form>

      <div className="modal-content-footer mt-3">
        <div className="footer_button_area gap-x-16">
          <button className="footer-btn button_back" onClick={() => onClose()}>
            キャンセル{' '}
          </button>
          <SpinnerButton type="submit" name="設定" className="footer-btn button_base" onClick={() => handleSubmit()} />
        </div>
      </div>
    </>
  )
}

export default CategoryNameModalContent
